import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from '../../hooks/use-toast';
import {
  Car,
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  Calendar,
  Shield,
  FileText,
  Loader2,
  Search,
  MapPin,
  Weight
} from 'lucide-react';
import { getVehicleTypeLabel, getVehicleTypeIcon } from '../../constants/vehicleTypes';
import VehicleTypeSelector from '../../components/VehicleTypeSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Zones géographiques
const ZONES = [
  { value: 'abidjan', label: 'Abidjan' },
  { value: 'bouake', label: 'Bouaké' },
  { value: 'yamoussoukro', label: 'Yamoussoukro' },
  { value: 'san_pedro', label: 'San-Pédro' },
  { value: 'korhogo', label: 'Korhogo' },
  { value: 'man', label: 'Man' },
  { value: 'daloa', label: 'Daloa' },
  { value: 'national', label: 'National' },
  { value: 'international', label: 'International' }
];

// Statuts de disponibilité
const AVAILABILITY_STATUS = [
  { value: 'available', label: 'Disponible' },
  { value: 'in_mission', label: 'En mission' },
  { value: 'maintenance', label: 'En maintenance' },
  { value: 'unavailable', label: 'Indisponible' }
];

const DriverVehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    plate: '',
    type: '',
    brand: '',
    model: '',
    year: '',
    capacity: '',
    maxWeight: '',
    zone: '',
    availability: 'available',
    insuranceNumber: '',
    insuranceExpiry: '',
    technicalVisitExpiry: ''
  });

  const fetchVehicles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/driver/vehicles`, {});
      setVehicles(response.data.vehicles || []);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  const resetForm = () => {
    setFormData({
      plate: '',
      type: '',
      brand: '',
      model: '',
      year: '',
      capacity: '',
      maxWeight: '',
      zone: '',
      availability: 'available',
      insuranceNumber: '',
      insuranceExpiry: '',
      technicalVisitExpiry: ''
    });
    setEditingVehicle(null);
  };

  const openAddDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      plate: vehicle.plate || '',
      type: vehicle.type || '',
      brand: vehicle.brand || '',
      model: vehicle.model || '',
      year: vehicle.year?.toString() || '',
      capacity: vehicle.capacity?.toString() || '',
      maxWeight: vehicle.maxWeight?.toString() || '',
      zone: vehicle.zone || '',
      availability: vehicle.availability || 'available',
      insuranceNumber: vehicle.insuranceNumber || '',
      insuranceExpiry: vehicle.insuranceExpiry || '',
      technicalVisitExpiry: vehicle.technicalVisitExpiry || ''
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.plate || !formData.type) {
      toast({ title: 'Erreur', description: 'Immatriculation et type sont requis', variant: 'destructive' });
      return;
    }

    try {
      setActionLoading(true);
      const payload = {
        ...formData,
        year: formData.year ? parseInt(formData.year) : null,
        capacity: formData.capacity ? parseFloat(formData.capacity) : null,
        maxWeight: formData.maxWeight ? parseFloat(formData.maxWeight) : null
      };

      if (editingVehicle) {
        await axios.put(
          `${API}/driver/vehicles/${editingVehicle.id}`,
          payload,
          {}
        );
        toast({ title: 'Succès', description: 'Véhicule mis à jour' });
      } else {
        await axios.post(
          `${API}/driver/vehicles`,
          payload,
          {}
        );
        toast({ title: 'Succès', description: 'Véhicule ajouté' });
      }

      setShowDialog(false);
      resetForm();
      fetchVehicles();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Une erreur est survenue',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (vehicleId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce véhicule ?')) return;

    try {
      await axios.delete(`${API}/driver/vehicles/${vehicleId}`, {});
      toast({ title: 'Succès', description: 'Véhicule supprimé' });
      fetchVehicles();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Erreur lors de la suppression',
        variant: 'destructive'
      });
    }
  };

  const isExpiringSoon = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = (date - now) / (1000 * 60 * 60 * 24);
    return diffDays < 30 && diffDays > 0;
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  // Filtrer les véhicules par recherche
  const filteredVehicles = vehicles.filter(v => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      v.plate?.toLowerCase().includes(query) ||
      v.type?.toLowerCase().includes(query) ||
      v.brand?.toLowerCase().includes(query) ||
      v.model?.toLowerCase().includes(query) ||
      v.zone?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#1db584]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg md:text-2xl font-bold text-gray-900">Ma Flotte</h1>
          <p className="text-gray-600">{vehicles.length} véhicule(s) enregistré(s)</p>
        </div>
        <Button onClick={openAddDialog} className="bg-[#1db584] hover:bg-[#16a075]">
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un véhicule
        </Button>
      </div>

      {/* Barre de recherche */}
      {vehicles.length > 0 && (
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Rechercher par immatriculation, type, marque..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      )}

      {vehicles.length === 0 ? (
        <Card className="p-8 text-center">
          <Car className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500 mb-4">Aucun véhicule enregistré</p>
          <Button onClick={openAddDialog}>
            <Plus className="w-4 h-4 mr-2" />
            Ajouter votre premier véhicule
          </Button>
        </Card>
      ) : filteredVehicles.length === 0 ? (
        <Card className="p-8 text-center">
          <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">Aucun véhicule ne correspond à votre recherche</p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredVehicles.map((vehicle) => (
            <Card key={vehicle.id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[#1db584]/10 flex items-center justify-center text-2xl">
                    {getVehicleTypeIcon(vehicle.type)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-lg text-gray-900">{vehicle.plate}</h3>
                      {vehicle.availability === 'available' && (
                        <Badge className="bg-green-100 text-green-800 text-xs">Disponible</Badge>
                      )}
                      {vehicle.availability === 'in_mission' && (
                        <Badge className="bg-blue-100 text-blue-800 text-xs">En mission</Badge>
                      )}
                      {vehicle.availability === 'maintenance' && (
                        <Badge className="bg-amber-100 text-amber-800 text-xs">Maintenance</Badge>
                      )}
                    </div>
                    <p className="text-gray-600">{getVehicleTypeLabel(vehicle.type)}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                      {vehicle.brand && <span>{vehicle.brand} {vehicle.model}</span>}
                      {vehicle.year && <span>• {vehicle.year}</span>}
                      {vehicle.capacity && (
                        <span className="flex items-center gap-1">
                          <Weight className="w-3 h-3" /> {vehicle.capacity}T
                        </span>
                      )}
                      {vehicle.zone && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {ZONES.find(z => z.value === vehicle.zone)?.label || vehicle.zone}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => openEditDialog(vehicle)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(vehicle.id)} className="text-red-600 hover:text-red-700">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Documents Status */}
              <div className="mt-4 pt-4 border-t flex items-center gap-6">
                {vehicle.insuranceExpiry && (
                  <div className={`flex items-center gap-2 text-sm ${
                    isExpired(vehicle.insuranceExpiry) ? 'text-red-600' :
                    isExpiringSoon(vehicle.insuranceExpiry) ? 'text-amber-600' : 'text-gray-500'
                  }`}>
                    <Shield className="w-4 h-4" />
                    <span>Assurance: {new Date(vehicle.insuranceExpiry).toLocaleDateString('fr-FR')}</span>
                    {isExpired(vehicle.insuranceExpiry) && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Expirée</span>}
                    {isExpiringSoon(vehicle.insuranceExpiry) && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Bientôt</span>}
                  </div>
                )}
                {vehicle.technicalVisitExpiry && (
                  <div className={`flex items-center gap-2 text-sm ${
                    isExpired(vehicle.technicalVisitExpiry) ? 'text-red-600' :
                    isExpiringSoon(vehicle.technicalVisitExpiry) ? 'text-amber-600' : 'text-gray-500'
                  }`}>
                    <FileText className="w-4 h-4" />
                    <span>Visite technique: {new Date(vehicle.technicalVisitExpiry).toLocaleDateString('fr-FR')}</span>
                    {isExpired(vehicle.technicalVisitExpiry) && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Expirée</span>}
                    {isExpiringSoon(vehicle.technicalVisitExpiry) && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Bientôt</span>}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingVehicle ? 'Modifier le véhicule' : 'Ajouter un véhicule'}</DialogTitle>
            <DialogDescription>
              {editingVehicle ? 'Modifiez les informations du véhicule' : 'Enregistrez un nouveau véhicule dans votre flotte'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Immatriculation *</Label>
                <Input
                  placeholder="AB 1234 CD"
                  value={formData.plate}
                  onChange={(e) => setFormData({ ...formData, plate: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Type de véhicule *</Label>
                <VehicleTypeSelector
                  value={formData.type}
                  onChange={(value) => setFormData({ ...formData, type: value })}
                  placeholder="Sélectionner un type"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Marque</Label>
                <Input
                  placeholder="Mercedes, Renault..."
                  value={formData.brand}
                  onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Modèle</Label>
                <Input
                  placeholder="Actros, T-Range..."
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Année</Label>
                <Input
                  type="number"
                  placeholder="2020"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Capacité (tonnes)</Label>
                <Input
                  type="number"
                  placeholder="25"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Poids max (kg)</Label>
                <Input
                  type="number"
                  placeholder="38000"
                  value={formData.maxWeight}
                  onChange={(e) => setFormData({ ...formData, maxWeight: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Zone d'activité</Label>
                <Select value={formData.zone} onValueChange={(value) => setFormData({ ...formData, zone: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner une zone" />
                  </SelectTrigger>
                  <SelectContent>
                    {ZONES.map((zone) => (
                      <SelectItem key={zone.value} value={zone.value}>{zone.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Disponibilité</Label>
                <Select value={formData.availability} onValueChange={(value) => setFormData({ ...formData, availability: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABILITY_STATUS.map((status) => (
                      <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>N° Assurance</Label>
              <Input
                placeholder="Numéro de police d'assurance"
                value={formData.insuranceNumber}
                onChange={(e) => setFormData({ ...formData, insuranceNumber: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Expiration assurance</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="date"
                    value={formData.insuranceExpiry}
                    onChange={(e) => setFormData({ ...formData, insuranceExpiry: e.target.value })}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Expiration visite technique</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="date"
                    value={formData.technicalVisitExpiry}
                    onChange={(e) => setFormData({ ...formData, technicalVisitExpiry: e.target.value })}
                    className="pl-10"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubmit} disabled={actionLoading} className="bg-[#1db584]">
              {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {editingVehicle ? 'Enregistrer' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriverVehicles;
